/**
 * Grafana Faro bootstrap for the SPA.
 *
 * One-time initialization of the Faro Web SDK. Captures:
 *   - unhandled JS errors / promise rejections
 *   - React error-boundary failures (if present)
 *   - Core Web Vitals + page-load timings
 *   - distributed traces (via `@grafana/faro-web-tracing`), with
 *     `traceparent` injected into outbound `fetch` calls to the
 *     API so the frontend span joins the backend span in Tempo.
 *
 * Signals ship via the Grafana Cloud Frontend Observability
 * ingest URL. Alloy is not involved on this path — Faro posts
 * directly to Grafana Cloud over HTTPS. CORS / auth are handled
 * by the collector URL's own token.
 *
 * Safe-by-construction:
 *   - Returns immediately if `faroEnabled` is not set in the runtime config.
 *   - Returns immediately if `faroCollectorUrl` is empty.
 *   - Returns immediately on any route whose capability credential
 *     rides in the URL (wallboard/exeter `?key=`, shared previews and
 *     public estimates with a path token) — Faro's default page-view
 *     instrumentation captures the full URL including query params,
 *     which would ship the credential to the log store. The list lives
 *     in the route manifest (`isUrlCredentialRoutePath`) so a new
 *     token-gated route is covered by the same declaration that
 *     creates it. The check happens at init time because Faro's
 *     instrumentations are register-once-then-listen and there is
 *     no reliable per-event scrub hook in this SDK version.
 *   - The caller wraps the whole invocation in try/catch so any
 *     SDK-level failure prints to `console.error` but never
 *     breaks the application bootstrap.
 *
 * House style for the frontend logging convention is documented in
 * `.claude/rules/logging.md`. tl;dr: use stable event names like
 * `customers.list.failed`, pass identifiers via the attributes
 * object, never interpolate them into the message string.
 */

import { initializeFaro, getWebInstrumentations } from "@grafana/faro-web-sdk";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import { getRuntimeConfig } from "@shared-core/config/runtime-config";
import { isUrlCredentialRoutePath } from "@shared-core/routing/routes";

const APP_NAME = "customer-intelligence-frontend";

export function initFaro(): void {
  // Read inside the function, not at module scope: configuration resolves
  // during bootstrap and this module sits in main.tsx's static import graph.
  const {
    faroEnabled,
    faroCollectorUrl: COLLECTOR_URL,
    appVersion: APP_VERSION,
    environment: ENVIRONMENT,
    apiBaseUrl: API_BASE_URL,
  } = getRuntimeConfig();

  if (!faroEnabled) return;
  if (!COLLECTOR_URL) {
    console.warn(
      "[faro] faroEnabled is set but the collector URL is empty; skipping init.",
    );
    return;
  }
  if (
    typeof window !== "undefined" &&
    isUrlCredentialRoutePath(window.location.pathname)
  ) {
    // This route carries its capability credential in the URL (path
    // token or ?key=). Skip Faro entirely on these surfaces — see the
    // file header for the rationale.
    return;
  }

  // Propagate `traceparent` only to the API base URL so we never
  // leak trace context to third-party services. The regex matches
  // any request whose URL starts with the configured base.
  const propagateTargets: (string | RegExp)[] = API_BASE_URL
    ? [new RegExp(`^${escapeRegExp(API_BASE_URL)}`)]
    : [];

  initializeFaro({
    url: COLLECTOR_URL,
    app: {
      name: APP_NAME,
      version: APP_VERSION,
      environment: ENVIRONMENT,
    },
    logArgsSerializer: serializeConsoleArgs,
    consoleInstrumentation: { errorSerializer: serializeConsoleArgs },
    instrumentations: [
      ...getWebInstrumentations(),
      new TracingInstrumentation({
        instrumentationOptions: {
          propagateTraceHeaderCorsUrls: propagateTargets,
        },
      }),
    ],
  });
}

const MAX_CONSOLE_FIELDS_CHARS = 500;

/**
 * A captured console call as one line. Faro's default runs String() on every argument, so the house
 * `console.warn("event.name", { fields })` shape reached Loki as "event.name [object Object]". A plain-object
 * argument is written as JSON (cut short); anything else, errors included, keeps its String() form.
 */
export function serializeConsoleArgs(args: unknown[]): string {
  return args.map(serializeConsoleArg).join(" ");
}

function serializeConsoleArg(arg: unknown): string {
  try {
    if (
      arg !== null &&
      typeof arg === "object" &&
      Object.getPrototypeOf(arg) === Object.prototype
    ) {
      const json = JSON.stringify(arg);
      return json.length > MAX_CONSOLE_FIELDS_CHARS
        ? `${json.slice(0, MAX_CONSOLE_FIELDS_CHARS - 1)}…`
        : json;
    }
    return String(arg);
  } catch {
    return "";
  }
}

function escapeRegExp(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
